import { useState, useRef, Fragment } from 'react';
import { isMobile } from 'react-device-detect';
import dynamic from 'next/dynamic';
import axios from 'axios';
import QRCode from 'react-qr-code';
import Input from '../Input';
import Textarea from '../Textarea';
import Button from '../Button';
import { useAppProvider } from '../../context/app';
import { useLocationState } from '../../context/location';
import { copyToClipboard, getPriceDiff, phoneToString, sleep, toMoney } from '../../helpers';
import { submitClientRequest } from '../../helpers/clientRequest';
import styles from '../../styles/modules/object/ObjectInformationOwner.module.scss';
import FormAgreement from '../FormAgreement';

const Modal = dynamic(() => import('../Modal'));

const ObjectInformationOwner = ({ data }) => {
  const [appState, setAppState] = useAppProvider();
  const [isLoading, setIsLoading] = useState(false);
  const locationState = useLocationState();
  const [ownerPhone, setOwnerPhone] = useState(false);
  const [isModalShareVisible, setIsModalShareVisible] = useState(false);
  const [isModalCallbackVisible, setIsModalCallbackVisible] = useState(false);
  const [isModalCallbackSuccessVisible, setIsModalCallbackSuccessVisible] = useState(false);
  const [clientName, setClientName] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [clientComment, setClientComment] = useState('');
  const [agreeWithPolicy, setAgreeWithPolicy] = useState(false);
  const clientNameId = useRef();
  const clientPhoneId = useRef();
  const m2Price = useRef(
    data.type === 'продажа' && data.object.area.total && data.object.area.total.value !== 0
      ? Math.round(data.price.value / data.object.area.total.value)
      : null
  ).current;
  const sotPrice = useRef(
    data.type === 'продажа' &&
      data.category !== 'дом' &&
      data.object.area['lot-area'] &&
      data.object.area['lot-area'].value !== 0
      ? Math.round(data.price.value / data.object.area['lot-area'].value)
      : null
  ).current;

  const handlePhoneShowClick = async () => {
    if (ownerPhone) return;

    try {
      setIsLoading(true);
      await sleep(300);

      const response = await axios.get(
        process.env.NEXT_PUBLIC_API + `/contact/${data['sales-agent'].callId}`
      );

      if (!response.data || response.data.status !== 'ok') throw new Error(response.data.text);

      setIsLoading(false);
      setOwnerPhone(phoneToString(response.data.data.phone.toString()));
    } catch (error) {}
  };
  const handleFormSubmit = () => {
    clientNameId.current.setError(clientName.length <= 3);
    clientPhoneId.current.setError(clientPhone.length !== 11);

    if (clientName.length > 3 && clientPhone.length === 11 && agreeWithPolicy) {
      submitClientRequest({
        clientName,
        clientPhone: clientPhone,
        clientComment,
        adNumber: data['ads-number'],
        pathName: `/object/${data['ads-number']}`,
        whatComponent: 'ObjectInformationOwner',
        location: locationState.name,
        platform: 'web'
      }).then((isSubmitted) => {
        if (isSubmitted) {
          setIsModalCallbackVisible(false);
          setIsModalCallbackSuccessVisible(true);
        }
      });
    }
  };
  const getTitle = () => {
    const finalTitle = [];

    finalTitle.push(data.type === 'продажа' ? 'Продается' : 'Сдается');

    switch (data.category) {
      default:
      case 'квартира':
        if (data.object.studio) finalTitle.push('квартира-студия');
        else {
          if (data.object.rooms === 1) finalTitle.push('однокомнатная');
          else if (data.object.rooms === 2) finalTitle.push('двухкомнатная');
          else if (data.object.rooms === 3) finalTitle.push('трехкомнатная');
          else if (data.object.rooms === 4) finalTitle.push('четырехкомнатная');
          else if (data.object.rooms === 5) finalTitle.push('пятикомнатная');

          finalTitle.push('квартира');
        }

        break;

      case 'комната':
        if (data.object['rooms-offered'] === 1) finalTitle.push('одна', 'комната');
        else if (data.object.rooms === 2) finalTitle.push('две', 'комнаты');
        else if (data.object.rooms === 3) finalTitle.push('три', 'комнаты');
        else if (data.object.rooms === 4) finalTitle.push('четыре', 'комнаты');
        else if (data.object.rooms === 5) finalTitle.push('пять', 'комнат');
        else finalTitle.push('комната');

        break;

      case 'дом':
        if (data.object['floors-total'] === 1) finalTitle.push('одноэтажный', 'дом');
        else if (data.object['floors-total'] === 2) finalTitle.push('двухэтажный', 'дом');
        else if (data.object['floors-total'] === 3) finalTitle.push('трехэтажный', 'дом');
        else if (data.object['floors-total'] === 4) finalTitle.push('четырехэтажный', 'дом');
        else if (data.object['floors-total'] === 5) finalTitle.push('пятиэтажный', 'дом');
        else finalTitle.push('дом');

        break;

      case 'участок':
        finalTitle.push('участок');

        break;

      case 'коммерция':
        finalTitle.push('коммерческий объект');

        break;
    }

    return finalTitle.join(' ');
  };
  const handleShareClick = () => {
    if (navigator.share && isMobile) {
      navigator.share({
        title: document.title,
        text: getTitle(),
        url: `https://landlord.ru/object/${data['ads-number']}`
      });
    } else setIsModalShareVisible(true);
  };
  const handleFavoriteClick = () => {
    let newFavorites;

    if (appState.favorites.includes(data['ads-number'].toString())) {
      newFavorites = appState.favorites.filter((x) => x !== data['ads-number'].toString());
    } else {
      newFavorites = [data['ads-number'].toString(), ...appState.favorites];
    }

    setAppState({
      type: 'SET_APP',
      payload: {
        ...appState,
        favorites: newFavorites
      }
    });
    localStorage.setItem('favorites', JSON.stringify(newFavorites));
  };

  return (
    <Fragment>
      <Modal
        title="Поделиться объектом"
        button="Скопировать ссылку"
        action={(e) => {
          copyToClipboard(`https://landlord.ru/object/${data['ads-number']}`);

          e.target.innerText = 'Ссылка скопирована';
          e.target.style.cssText = 'opacity: 0.4; pointer-events: none;';
          setTimeout(() => {
            e.target.innerText = 'Скопировать ссылку';
            e.target.style.cssText = '';
          }, 2000);
        }}
        visibility={isModalShareVisible}
        onClose={() => setIsModalShareVisible(false)}
      >
        <div style={{ marginBottom: 18 }}>Выберите удобный для Вас сервис и нажмите на него</div>
        <div className={styles.modalButtons}>
          <a
            className={[styles.modalButton, styles.vk].join(' ')}
            href={`https://vk.com/share.php?url=https://landlord.ru/object/${data['ads-number']}`}
            target="_blank"
            rel="noreferrer"
          />
          <a
            className={[styles.modalButton, styles.ok].join(' ')}
            href={`https://connect.ok.ru/offer?url=https://landlord.ru/object/${data['ads-number']}`}
            target="_blank"
            rel="noreferrer"
          />
          <a
            className={[styles.modalButton, styles.tg].join(' ')}
            href={`https://t.me/share/url?url=https://landlord.ru/object/${data['ads-number']}`}
            target="_blank"
            rel="noreferrer"
          />
        </div>
      </Modal>
      <Modal
        title="Заказать звонок"
        visibility={isModalCallbackSuccessVisible}
        onClose={() => setIsModalCallbackSuccessVisible(false)}
      >
        <div style={{ marginBottom: 18 }}>Спасибо за вашу заявку. Мы скоро с Вами свяжемся!</div>
        <img
          src="/images/thumbUp.png"
          alt=""
          style={{ padding: 50, width: '100%', boxSizing: 'border-box' }}
        />
      </Modal>
      <Modal
        title="Заказать звонок"
        button="Отправить"
        action={handleFormSubmit}
        visibility={isModalCallbackVisible}
        onClose={() => setIsModalCallbackVisible(false)}
        buttonDisabled={!agreeWithPolicy}
      >
        <div style={{ marginBottom: 18 }}>
          Оставьте свои контактные данные и мы с Вами свяжемся в самые кратчайшие сроки, ответим на
          все вопросы!
        </div>
        <Input
          title="Имя"
          inputs={[
            {
              placeholder: 'Василий Петрович',
              value: clientName,
              type: 'text'
            }
          ]}
          isWrappedStyle
          ref={clientNameId}
          onChange={(v) => setClientName(v[0])}
        />
        <Input
          title="Телефон"
          inputs={[
            {
              placeholder: locationState.phone,
              value: clientPhone,
              mask: 'phone',
              type: 'tel'
            }
          ]}
          isWrappedStyle
          ref={clientPhoneId}
          onChange={(v) => setClientPhone(v[0])}
        />
        <Textarea
          title="Коммментарий"
          placeholder="Например, «Интересует ваш объект, есть несколько вопросов»"
          autoHeight
          isWrappedStyle
          onChange={(v) => setClientComment(v)}
        />
        <FormAgreement onChange={setAgreeWithPolicy} />
      </Modal>
      <section className={styles.column}>
        <div className={styles.objectInfo}>
          <div className={styles.objectInfoPrice}>
            {getPriceDiff(data.type, data.price) && getPriceDiff(data.type, data.price).icon}
            {toMoney(data.price.value)} {data.price.currency}
          </div>
          {getPriceDiff(data.type, data.price) && (
            <div className={styles.objectInfoOldPrice}>
              {toMoney(getPriceDiff(data.type, data.price).value)} {data.price.currency}
            </div>
          )}
          {m2Price && (
            <div className={styles.objectInfoSubprice}>
              {toMoney(m2Price)} {data.price.currency}/{data.object.area.total.unit}
            </div>
          )}
          {sotPrice && (
            <div className={styles.objectInfoSubprice}>
              {toMoney(sotPrice)} {data.price.currency}/{data.object.area['lot-area'].unit}
            </div>
          )}
        </div>
        <div className={styles.ownerInfo}>
          <div className={styles.ownerInfoContent}>
            {data['sales-agent'].name && (
              <div className={styles.ownerAgent}>
                {data['sales-agent'].avatar && (
                  <img
                    src={data['sales-agent'].avatar}
                    alt={data['sales-agent'].name}
                    className={styles.ownerAvatar}
                  />
                )}
                <span className={styles.ownerName}>{data['sales-agent'].name}</span>
              </div>
            )}
            <div className={styles.ownerInfoOffice}>{data['sales-agent'].office}</div>
            <div className={styles.ownerInfoGroup}>
              {ownerPhone && !isMobile && (
                <QRCode className={styles.qr} value={`tel:${ownerPhone}`} size={46} />
              )}
              <Button
                onClick={handlePhoneShowClick}
                type="external"
                href={ownerPhone && 'tel:' + ownerPhone}
                loading={isLoading}
                color="green"
                fullWidth
              >
                {ownerPhone || 'Показать телефон'}
              </Button>
              <button type="button" className={styles.share} onClick={handleShareClick}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="18" cy="5" r="3" />
                  <circle cx="6" cy="12" r="3" />
                  <circle cx="18" cy="19" r="3" />
                  <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                  <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                </svg>
              </button>
            </div>
            <Button
              fullWidth
              color="blue"
              style={{ marginTop: 8 }}
              onClick={() => setIsModalCallbackVisible(true)}
            >
              Заказать звонок
            </Button>
            {data.type === 'продажа' && data.mortgage.payment > 0 && (
              <Button
                color="gray"
                style={{ marginTop: 8 }}
                onClick={() => {
                  document
                    .getElementById('mortgage')
                    ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                fullWidth
              >
                В ипотеку от {toMoney(data.mortgage.payment)} ₽/мес
              </Button>
            )}
          </div>
        </div>
        <button
          className={styles.objectFavorites}
          type="button"
          onClick={handleFavoriteClick}
          style={{
            color: appState.favorites.includes(data['ads-number'].toString())
              ? '#fb5f5f'
              : 'inherit'
          }}
        >
          {appState.favorites.includes(data['ads-number'].toString())
            ? 'Удалить из избранного'
            : 'Добавить в избранное'}
        </button>
        <button className={styles.objectShare} type="button" onClick={handleShareClick}>
          Поделиться объектом
        </button>
      </section>
    </Fragment>
  );
};

export default ObjectInformationOwner;
